import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCachedBrands, formatBrandLabel } from './brandTheme';
import { normalizeBillInvoiceNumber } from './billInvoiceNumber';
import { invoiceCollectorName } from './collectorPrintName';
import {
  purchaserTaxAddress,
  purchaserTaxName,
  purchaserTaxPhone,
  supplierTaxAddress,
} from './customerTaxUtils';

const MARGIN = 14;
const BLACK = [0, 0, 0];
const VAT_RATE = 0.18;

function display(v) {
  const s = String(v ?? '').trim();
  return s || '';
}

function formatAmount(n) {
  return new Intl.NumberFormat('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function formatDisplayDate(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

/** Print stamp matching typical invoice footers, e.g. 04/09/2026 10:08:41PM */
function formatPrintTimestamp(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const hh = String(hours).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${minutes}:${seconds}${ampm}`;
}

function drawInvoiceAcknowledgement(doc, startY, generatedAt) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  let y = startY + 4;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  const ack = 'Received the above goods in correct quantity and in good condition.';
  const ackLines = doc.splitTextToSize(ack, contentWidth);
  doc.text(ackLines, MARGIN, y);
  y += ackLines.length * 5 + 16;

  const colW = contentWidth / 2;
  const lineW = Math.min(72, colW - 8);
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.35);
  doc.line(MARGIN, y, MARGIN + lineW, y);
  doc.line(MARGIN + colW, y, MARGIN + colW + lineW, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Customer Signature & Rubber Stamp', MARGIN, y);
  doc.text('Approved By', MARGIN + colW, y);
  y += 8;

  const stamp = formatPrintTimestamp(generatedAt);
  if (stamp) {
    doc.setFontSize(8);
    doc.text(stamp, MARGIN, y);
    y += 4;
  }
  return y;
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function lineTotal(bags, unitPrice) {
  const b = Number(bags) || 0;
  const u = Number(unitPrice) || 0;
  return roundMoney(b * u);
}

/** Selling prices are VAT-inclusive. Supply = total / 1.18 so supply + 18% VAT equals the invoice total. */
function valueOfSupplyFromInclusive(inclusiveTotal) {
  return roundMoney((Number(inclusiveTotal) || 0) / (1 + VAT_RATE));
}

function buildBillLineItems(bill) {
  const brands = getCachedBrands();
  const items = [];
  for (const brand of brands) {
    const bags = Number(bill[`${brand.key}Bags`]) || 0;
    if (bags <= 0) continue;
    const unitPrice = Number(bill[`${brand.key}UnitPrice`]) || 0;
    items.push({
      reference: String(bill.stockId ?? '').trim() || '—',
      description: formatBrandLabel(brand) || brand.label,
      quantity: bags,
      unitPrice,
      inclusiveAmount: lineTotal(bags, unitPrice),
    });
  }
  return items;
}

function sumInvoiceDiscountForBill(promotions, billId) {
  const id = String(billId ?? '').trim();
  if (!id) return 0;
  let sum = 0;
  for (const row of Array.isArray(promotions) ? promotions : []) {
    const type = String(row?.type ?? '').trim();
    if (type !== 'invoice_discount' && type !== 'rule_cashback') continue;
    if (String(row.billId ?? '').trim() !== id) continue;
    sum += Number(row.discountAmount) || 0;
  }
  return Math.round(sum * 100) / 100;
}

function drawBox(doc, x, y, w, h) {
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.35);
  doc.rect(x, y, w, h);
}

function labelColon(label) {
  const base = String(label ?? '')
    .trim()
    .replace(/\s*:\s*$/, '');
  return `${base} : `;
}

function drawLabeledField(doc, label, value, x, y, w, h) {
  drawBox(doc, x, y, w, h);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  const labelText = labelColon(label);
  doc.text(labelText, x + 2, y + 6);
  const labelW = doc.getTextWidth(labelText);
  const valLines = doc.splitTextToSize(display(value) || '—', w - labelW - 4);
  doc.text(valLines[0] ?? '—', x + 2 + labelW, y + 6);
}

function drawPartyBox(doc, title, fields, x, y, w, h) {
  drawBox(doc, x, y, w, h);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(title, x + 2, y + 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  let cy = y + 9;
  for (const { label, value } of fields) {
    const labelText = labelColon(label);
    doc.setFont('helvetica', 'bold');
    doc.text(labelText, x + 2, cy);
    doc.setFont('helvetica', 'normal');
    const labelW = doc.getTextWidth(labelText);
    const valLines = doc.splitTextToSize(display(value) || '—', w - labelW - 4);
    doc.text(valLines[0] ?? '—', x + 2 + labelW, cy);
    cy += 5.5;
  }
}

const BELOW_TWENTY = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];

const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function wordsUnder1000(n) {
  const num = Math.floor(n);
  if (num === 0) return '';
  if (num < 20) return BELOW_TWENTY[num];
  if (num < 100) {
    const t = Math.floor(num / 10);
    const r = num % 10;
    return r ? `${TENS[t]} ${BELOW_TWENTY[r]}` : TENS[t];
  }
  const h = Math.floor(num / 100);
  const r = num % 100;
  return r ? `${BELOW_TWENTY[h]} Hundred ${wordsUnder1000(r)}` : `${BELOW_TWENTY[h]} Hundred`;
}

function integerToWords(n) {
  const num = Math.floor(Math.abs(n));
  if (num === 0) return 'Zero';
  const parts = [];
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const rest = num % 1000;
  if (crore) parts.push(`${wordsUnder1000(crore)} Crore`);
  if (lakh) parts.push(`${wordsUnder1000(lakh)} Lakh`);
  if (thousand) parts.push(`${wordsUnder1000(thousand)} Thousand`);
  if (rest) parts.push(wordsUnder1000(rest));
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function amountInWords(amount) {
  const n = Math.round((Number(amount) || 0) * 100) / 100;
  const rupees = Math.floor(n);
  const cents = Math.round((n - rupees) * 100);
  let text = `${integerToWords(rupees)} Rupee${rupees === 1 ? '' : 's'}`;
  if (cents > 0) {
    text += ` and ${integerToWords(cents)} Cent${cents === 1 ? '' : 's'}`;
  }
  return `${text} Only`;
}

function resolveDeliveryDate(bill, unloads) {
  const billId = String(bill.id ?? '').trim();
  for (const row of Array.isArray(unloads) ? unloads : []) {
    if (String(row.billId ?? '').trim() === billId) {
      return formatDisplayDate(row.date || row.unloadDate);
    }
    if (String(row.id ?? '').trim() === String(bill.unloadRequestId ?? '').trim()) {
      return formatDisplayDate(row.date || row.unloadDate);
    }
  }
  return formatDisplayDate(bill.date);
}

function renderTaxInvoicePage(doc, bill, opts) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  let y = MARGIN;

  const shop = opts.shop || {};
  const customer = opts.customer || {};
  const invoiceNo = normalizeBillInvoiceNumber(bill.invoiceNumber) || '—';
  const invoiceDate = formatDisplayDate(bill.date);
  const deliveryDate = resolveDeliveryDate(bill, opts.unloads);
  const placeOfSupply = display(customer.placeOfSupply || customer.location);
  const additionalInfo = display(customer.taxAdditionalInfo);

  drawBox(doc, MARGIN, y, contentWidth, 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Tax Invoice', pageWidth / 2, y + 7, { align: 'center' });
  y += 10;

  const halfW = contentWidth / 2;
  drawLabeledField(doc, 'Date of Invoice:', invoiceDate, MARGIN, y, halfW, 10);
  drawLabeledField(doc, 'Tax Invoice No.:', invoiceNo, MARGIN + halfW, y, halfW, 10);
  y += 10;

  const partyH = 34;
  drawPartyBox(
    doc,
    "Supplier's",
    [
      { label: 'TIN', value: shop.supplierTin },
      { label: 'Name', value: shop.shopName },
      { label: 'Address', value: supplierTaxAddress(shop) },
      { label: 'Telephone No', value: shop.contactNumber },
    ],
    MARGIN,
    y,
    halfW,
    partyH,
  );
  drawPartyBox(
    doc,
    "Purchaser's",
    [
      { label: 'TIN', value: customer.purchaserTin },
      { label: 'Name', value: purchaserTaxName(customer) },
      { label: 'Address', value: purchaserTaxAddress(customer) },
      { label: 'Telephone No', value: purchaserTaxPhone(customer) },
    ],
    MARGIN + halfW,
    y,
    halfW,
    partyH,
  );
  y += partyH;

  drawLabeledField(doc, 'Date of Delivery:', deliveryDate, MARGIN, y, halfW, 10);
  drawLabeledField(doc, 'Place of Supply:', placeOfSupply, MARGIN + halfW, y, halfW, 10);
  y += 10;

  drawLabeledField(doc, 'Additional Information if any:', additionalInfo, MARGIN, y, contentWidth, 12);
  y += 12;

  const lineItems = buildBillLineItems(bill);
  const invoiceDiscount = sumInvoiceDiscountForBill(opts.promotions, bill.id);
  const grossInclusive =
    lineItems.length > 0
      ? lineItems.reduce((sum, row) => sum + row.inclusiveAmount, 0)
      : Number(bill.totalAmount) || 0;
  const invoiceTotal = Math.max(0, roundMoney(grossInclusive - invoiceDiscount));
  const valueOfSupply = valueOfSupplyFromInclusive(invoiceTotal);
  const vatAmount = roundMoney(invoiceTotal - valueOfSupply);
  const totalIncVat = invoiceTotal;

  const exVatLines = lineItems.map((row) => {
    const amountExVat = valueOfSupplyFromInclusive(row.inclusiveAmount);
    return {
      ...row,
      amountExVat,
      unitPriceExVat: row.quantity > 0 ? roundMoney(amountExVat / row.quantity) : valueOfSupplyFromInclusive(row.unitPrice),
    };
  });
  const discountExVat = valueOfSupplyFromInclusive(invoiceDiscount);
  if (exVatLines.length > 0) {
    const linedSum = roundMoney(
      exVatLines.reduce((sum, row) => sum + row.amountExVat, 0) - discountExVat,
    );
    const drift = roundMoney(valueOfSupply - linedSum);
    if (drift) {
      const last = exVatLines[exVatLines.length - 1];
      last.amountExVat = roundMoney(last.amountExVat + drift);
    }
  }

  const tableBody =
    exVatLines.length > 0
      ? exVatLines.map((row, i) => [
          row.reference || String(i + 1),
          row.description,
          String(row.quantity),
          formatAmount(row.unitPriceExVat),
          formatAmount(row.amountExVat),
        ])
      : [['—', 'Credit sale (cement bags)', '—', '—', formatAmount(valueOfSupply)]];

  if (invoiceDiscount > 0) {
    tableBody.push(['', 'Promotion discount', '', '', formatAmount(-discountExVat)]);
  }

  autoTable(doc, {
    startY: y,
    head: [
      [
        'Reference',
        'Description of Goods or Services',
        'Quantity',
        'Unit Price',
        'Amount Excluding VAT (Rs.)',
      ],
    ],
    body: tableBody,
    foot: [
      ['', 'Total Value of Supply:', '', '', formatAmount(valueOfSupply)],
      ['', `VAT Amount (Total Value of Supply @ ${VAT_RATE * 100}%):`, '', '', formatAmount(vatAmount)],
      ['', 'Total Amount including VAT:', '', '', formatAmount(totalIncVat)],
    ],
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8.5,
      textColor: BLACK,
      lineColor: BLACK,
      lineWidth: 0.35,
      cellPadding: 2,
      valign: 'middle',
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 8,
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'right',
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.14, halign: 'center' },
      1: { cellWidth: contentWidth * 0.36, halign: 'left' },
      2: { cellWidth: contentWidth * 0.12, halign: 'center' },
      3: { cellWidth: contentWidth * 0.18, halign: 'right' },
      4: { cellWidth: contentWidth * 0.2, halign: 'right' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  y = (doc.lastAutoTable?.finalY || y) + 4;

  const modeOfPayment = opts.modeOfPayment || 'Credit';
  drawLabeledField(
    doc,
    'Total Amount in words:',
    amountInWords(totalIncVat),
    MARGIN,
    y,
    contentWidth,
    12,
  );
  y += 12;
  drawLabeledField(doc, 'Mode of Payment:', modeOfPayment, MARGIN, y, contentWidth, 10);
  const ackY = drawInvoiceAcknowledgement(
    doc,
    y + 10,
    opts.generatedAt instanceof Date ? opts.generatedAt : new Date(),
  );
  const collectorName = invoiceCollectorName(bill);
  let endY = ackY || y + 10;
  if (collectorName && ackY) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BLACK);
    doc.text(`Collector : ${collectorName}`, MARGIN, ackY + 5);
    endY = ackY + 5;
  }

  const shopId = display(customer.id);
  if (shopId !== '—') {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (endY > pageHeight - 18) {
      doc.addPage();
      endY = 16;
    } else {
      endY += 8;
    }
    doc.setDrawColor(...BLACK);
    doc.setLineWidth(0.35);
    doc.line(MARGIN, endY, pageWidth - MARGIN, endY);
    endY += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BLACK);
    doc.text(`Shop ID : ${shopId}`, pageWidth / 2, endY, { align: 'center' });
  }
}

/**
 * Download a tax invoice PDF for a single credit bill.
 */
export function downloadTaxInvoicePdf(bill, opts = {}) {
  if (!bill) return;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  renderTaxInvoicePage(doc, bill, opts);
  const customerName = String(opts.customer?.name ?? bill.customerName ?? 'customer')
    .replace(/[^\w\-]+/g, '-')
    .slice(0, 40);
  const invoiceNo = normalizeBillInvoiceNumber(bill.invoiceNumber) || bill.id || 'invoice';
  const dateSlug = String(bill.date ?? '').slice(0, 10);
  doc.save(`tax-invoice-${customerName}-${invoiceNo}-${dateSlug}.pdf`);
}
